import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, Download, Trash2, Eye, Calendar, Image, Video, FileType, Link2, Copy, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

function FlyersManager() {
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fechaUso, setFechaUso] = useState('');
  const [fechaExpiracion, setFechaExpiracion] = useState('');
  const [categoria, setCategoria] = useState('difusion');
  const [previews, setPreviews] = useState([]);
  // Captions opcionales por archivo seleccionado (índice -> texto)
  const [captionTexts, setCaptionTexts] = useState({});
  // Edición de caption de un flyer existente
  const [editingCaption, setEditingCaption] = useState({}); // {flyer_id: text}
  const [savingCaptionId, setSavingCaptionId] = useState(null);
  const [showDesignerModal, setShowDesignerModal] = useState(false);
  const userStored = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = userStored.role !== 'worker';

  const [designerToken, setDesignerToken] = useState(null);
  const [designerUrl, setDesignerUrl] = useState('');

  useEffect(() => {
    loadFlyers();
  }, []);

  const loadFlyers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/flyers/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlyers(response.data.flyers);
    } catch (error) {
      console.error('Error cargando flyers:', error);
      toast.error('Error al cargar flyers');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setSelectedFiles(files);
    setCaptionTexts({}); // reset captions al cambiar archivos

    // Generar previews para todos los archivos
    const newPreviews = [];
    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews[index] = { type: 'image', url: reader.result, name: file.name };
          setPreviews([...newPreviews]);
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        newPreviews[index] = { type: 'video', url: URL.createObjectURL(file), name: file.name };
        setPreviews([...newPreviews]);
      }
    });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !fechaUso || !fechaExpiracion) {
      toast.error('Selecciona al menos un archivo, fecha de uso y fecha de expiración');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      let successCount = 0;
      let errorCount = 0;

      // Subir cada archivo
      for (let idx = 0; idx < selectedFiles.length; idx++) {
        const file = selectedFiles[idx];
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('fecha_uso', fechaUso);
          formData.append('fecha_expiracion', fechaExpiracion);
          formData.append('categoria', categoria);
          formData.append('uploaded_by', 'admin');
          formData.append('caption_text', (captionTexts[idx] || '').trim());

          await axios.post(`${API}/api/flyers/upload`, formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          successCount++;
        } catch (err) {
          console.error(`Error subiendo ${file.name}:`, err);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} flyer(s) subido(s) exitosamente`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} flyer(s) fallaron al subir`);
      }

      setSelectedFiles([]);
      setFechaUso('');
      setFechaExpiracion('');
      setCategoria('difusion');
      setPreviews([]);
      setCaptionTexts({});
      loadFlyers();
    } catch (error) {
      console.error('Error subiendo flyers:', error);
      toast.error('Error al subir flyers');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (flyerId, filename) => {
    if (!window.confirm(`¿Eliminar ${filename}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/flyers/${flyerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flyer eliminado');
      loadFlyers();
    } catch (error) {
      console.error('Error eliminando flyer:', error);
      toast.error('Error al eliminar flyer');
    }
  };

  const saveCaption = async (flyerId) => {
    try {
      setSavingCaptionId(flyerId);
      const token = localStorage.getItem('token');
      const newText = (editingCaption[flyerId] || '').trim();
      await axios.patch(
        `${API}/api/flyers/${flyerId}`,
        { caption_text: newText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(newText ? 'Texto guardado' : 'Texto eliminado');
      // Limpiar el editor para ese flyer y refrescar
      setEditingCaption(prev => {
        const c = { ...prev };
        delete c[flyerId];
        return c;
      });
      loadFlyers();
    } catch (error) {
      console.error('Error guardando caption:', error);
      toast.error('Error al guardar el texto');
    } finally {
      setSavingCaptionId(null);
    }
  };

  const handleBatchDownload = async (fechaUso, categoria = null) => {
    try {
      if (!fechaUso || fechaUso === 'Sin fecha') {
        toast.error('No se puede descargar flyers sin fecha de uso');
        return;
      }

      // Filtrar flyers por fecha y categoría
      const flyersToDownload = flyers.filter(f => {
        const matchFecha = f.fecha_uso === fechaUso;
        const matchCategoria = !categoria || f.categoria === categoria;
        return matchFecha && matchCategoria;
      });

      if (flyersToDownload.length === 0) {
        toast.error('No hay flyers para descargar');
        return;
      }

      toast.info(`Descargando ${flyersToDownload.length} archivo(s)...`);

      // Descargar cada archivo individualmente
      for (const flyer of flyersToDownload) {
        await handleDownload(flyer.id, flyer.original_filename);
        // Pequeña pausa entre descargas para evitar que el navegador las bloquee
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      toast.success(`${flyersToDownload.length} archivo(s) descargados`);
    } catch (error) {
      console.error('Error descargando batch:', error);
      toast.error('Error al descargar algunos archivos');
    }
  };


  const handleDownload = async (flyerId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/flyers/${flyerId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Descargando...');
    } catch (error) {
      console.error('Error descargando flyer:', error);
      toast.error('Error al descargar flyer');
    }
  };

  const generateDesignerToken = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/flyers/designer-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const baseUrl = window.location.origin;
      const fullUrl = `${baseUrl}${response.data.url}`;
      
      setDesignerToken(response.data.token);
      setDesignerUrl(fullUrl);
      setShowDesignerModal(true);
      toast.success('Token generado');
    } catch (error) {
      console.error('Error generando token:', error);
      toast.error('Error al generar token');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString('es-AR');
  };

  const isExpired = (fecha) => {
    return new Date(fecha) < new Date();
  };

  // Agrupar flyers por fecha de uso
  const flyersByDate = flyers.reduce((acc, flyer) => {
    const fecha = flyer.fecha_uso || 'Sin fecha';
    if (!acc[fecha]) {
      acc[fecha] = [];
    }
    acc[fecha].push(flyer);
    return acc;
  }, {});

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Gestión de Flyers</h1>
        {isAdmin && (
        <Button onClick={generateDesignerToken} className="flex items-center gap-2">
          <Link2 className="w-4 h-4" />
          Generar Enlace Diseñador
        </Button>
        )}
      </div>

      {/* Upload Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Subir Nuevo Flyer</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2 font-medium">Archivos (Selección Múltiple)</label>
              <input
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                multiple
                className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
              />
              <p className="mt-1 text-sm text-gray-500">
                ✨ Puedes seleccionar múltiples archivos a la vez<br/>
                Imágenes (JPG, PNG, GIF, WebP) hasta 10MB o Videos (MP4, MOV, AVI, MKV) hasta 50MB
              </p>

              {selectedFiles.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">
                    {selectedFiles.length} archivo(s) seleccionado(s):
                  </p>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="bg-blue-50 rounded p-3 border border-blue-200">
                        <div className="flex items-center gap-2 text-sm">
                          {file.type.startsWith('image/') ? (
                            <Image className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Video className="w-4 h-4 text-purple-600" />
                          )}
                          <span className="truncate flex-1 font-medium">{file.name}</span>
                          <span className="text-gray-500 text-xs">
                            {(file.size / (1024 * 1024)).toFixed(1)} MB
                          </span>
                        </div>
                        <div className="mt-2">
                          <label className="block text-xs text-gray-600 mb-1">
                            💬 Texto opcional (se enviará como caption debajo del flyer en WhatsApp)
                          </label>
                          <textarea
                            data-testid={`flyer-caption-input-${index}`}
                            value={captionTexts[index] || ''}
                            onChange={(e) => setCaptionTexts(prev => ({ ...prev, [index]: e.target.value }))}
                            placeholder="Ej: ¡Hoy 20hs MEGA PKO 75K garantizado! Entrá ahora a la sala 🃏"
                            rows={2}
                            className="block w-full text-sm px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
                          />
                          {(captionTexts[index] || '').length > 0 && (
                            <p className="mt-1 text-xs text-gray-500 text-right">
                              {(captionTexts[index] || '').length} caracteres
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium text-blue-700">📅 Fecha de Uso/Evento *</label>
                <input
                  type="date"
                  value={fechaUso}
                  onChange={(e) => setFechaUso(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="block w-full px-3 py-2 border-2 border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-sm text-blue-600 font-medium">
                  ¿Para qué día es este flyer? (ej: Domingo 17 de noviembre)
                </p>
              </div>

              <div>
                <label className="block mb-2 font-medium text-purple-700">🎯 Categoría *</label>
                <select
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  className="block w-full px-3 py-2 border-2 border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="difusion">📢 Difusión (Redes Sociales)</option>
                  <option value="app_pppoker">🃏 App PPPoker (Dimensiones específicas)</option>
                </select>
                <p className="mt-1 text-sm text-purple-600">
                  {categoria === 'app_pppoker' 
                    ? '⚠️ Para la app PPPoker - Usar dimensiones específicas'
                    : 'Para compartir en redes sociales y WhatsApp'}
                </p>
              </div>

              <div>
                <label className="block mb-2 font-medium">Fecha de Expiración</label>
                <input
                  type="date"
                  value={fechaExpiracion}
                  onChange={(e) => setFechaExpiracion(e.target.value)}
                  min={fechaUso || new Date().toISOString().split('T')[0]}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  El flyer se eliminará automáticamente después de esta fecha
                </p>
              </div>

              <Button
                onClick={handleUpload}
                disabled={selectedFiles.length === 0 || !fechaUso || !fechaExpiracion || uploading}
                className="mt-4 w-full"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Subiendo {selectedFiles.length} archivo(s)...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Subir {selectedFiles.length > 0 ? `${selectedFiles.length} Flyer(s)` : 'Flyer'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Flyers List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Flyers Disponibles ({flyers.length})</span>
            <Button variant="outline" size="sm" onClick={loadFlyers}>
              Actualizar
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando...</div>
          ) : flyers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay flyers disponibles
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(flyersByDate).map(([fecha, flyersGrupo]) => (
                <div key={fecha} className="space-y-4">
                  {/* Header de fecha con botones de descarga */}
                  <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border-2 border-blue-200">
                    <div>
                      <h3 className="text-lg font-bold text-blue-900">
                        📅 {formatDate(fecha)}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {flyersGrupo.length} flyer{flyersGrupo.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {fecha !== 'Sin fecha' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleBatchDownload(fecha)}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Todos
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBatchDownload(fecha, 'difusion')}
                          className="border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          📢 Difusión
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBatchDownload(fecha, 'app_pppoker')}
                          className="border-green-300 text-green-700 hover:bg-green-50"
                        >
                          🃏 App
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Grid de flyers */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {flyersGrupo.map((flyer) => (
                <div
                  key={flyer.id}
                  className={`border rounded-lg overflow-hidden ${
                    isExpired(flyer.fecha_expiracion) ? 'border-red-300 bg-red-50' : 'border-gray-200'
                  }`}
                >
                  <div className="h-48 bg-gray-100 flex items-center justify-center overflow-hidden relative">
                    {flyer.file_type === 'image' ? (
                      <img 
                        src={`${API}/api/flyers/${flyer.id}/download`}
                        alt={flyer.original_filename}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>';
                        }}
                      />
                    ) : (
                      <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                        <Video className="w-16 h-16 text-white opacity-50" />
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                          Video
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {flyer.file_type === 'image' ? (
                          <Image className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        ) : (
                          <Video className="w-4 h-4 text-purple-500 flex-shrink-0" />
                        )}
                        <span className="font-medium text-sm truncate">
                          {flyer.original_filename}
                        </span>
                      </div>
                      {/* Badge de categoría */}
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold flex-shrink-0 ${
                        flyer.categoria === 'app_pppoker' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {flyer.categoria === 'app_pppoker' ? '🃏 App' : '📢 Difusión'}
                      </span>
                    </div>
                    
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-1 text-gray-500">
                        <FileType className="w-3 h-3" />
                        {flyer.file_format.toUpperCase()} • {formatFileSize(flyer.file_size)}
                      </div>
                      {flyer.fecha_uso && (
                        <div className="flex items-center gap-1 text-blue-600 font-semibold bg-blue-50 px-2 py-1 rounded">
                          <Calendar className="w-3 h-3" />
                          Para: {formatDate(flyer.fecha_uso)}
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-gray-500">
                        <Calendar className="w-3 h-3" />
                        Expira: {formatDate(flyer.fecha_expiracion)}
                        {isExpired(flyer.fecha_expiracion) && (
                          <span className="text-red-600 font-medium">(Expirado)</span>
                        )}
                      </div>
                    </div>

                    {/* Caption editor (texto que va debajo del flyer en WhatsApp) */}
                    {(() => {
                      const isEditing = Object.prototype.hasOwnProperty.call(editingCaption, flyer.id);
                      const currentText = isEditing ? editingCaption[flyer.id] : (flyer.caption_text || '');
                      const hasCaption = (flyer.caption_text || '').trim().length > 0;
                      return (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                              💬 Texto en WhatsApp
                              {hasCaption && !isEditing && (
                                <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">activo</span>
                              )}
                            </label>
                            {!isEditing && (
                              <button
                                type="button"
                                data-testid={`flyer-caption-edit-btn-${flyer.id}`}
                                onClick={() => setEditingCaption(prev => ({ ...prev, [flyer.id]: flyer.caption_text || '' }))}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {hasCaption ? 'Editar' : '+ Agregar'}
                              </button>
                            )}
                          </div>
                          {isEditing ? (
                            <>
                              <textarea
                                data-testid={`flyer-caption-textarea-${flyer.id}`}
                                value={currentText}
                                onChange={(e) => setEditingCaption(prev => ({ ...prev, [flyer.id]: e.target.value }))}
                                placeholder="Texto que se enviará junto al flyer en WhatsApp"
                                rows={3}
                                className="block w-full text-xs px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 resize-y"
                              />
                              <div className="flex gap-2 mt-2">
                                <Button
                                  size="sm"
                                  data-testid={`flyer-caption-save-${flyer.id}`}
                                  onClick={() => saveCaption(flyer.id)}
                                  disabled={savingCaptionId === flyer.id}
                                  className="flex-1 h-7 text-xs"
                                >
                                  {savingCaptionId === flyer.id ? 'Guardando...' : 'Guardar'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingCaption(prev => {
                                    const c = { ...prev }; delete c[flyer.id]; return c;
                                  })}
                                  className="h-7 text-xs"
                                >
                                  Cancelar
                                </Button>
                              </div>
                            </>
                          ) : (
                            <p className={`text-xs whitespace-pre-wrap ${hasCaption ? 'text-gray-700 bg-gray-50 px-2 py-1.5 rounded' : 'text-gray-400 italic'}`}>
                              {hasCaption ? flyer.caption_text : 'Sin texto. El bot enviará el genérico de la fecha.'}
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    <div className="flex gap-2 mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(flyer.id, flyer.original_filename)}
                        className="flex-1"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(flyer.id, flyer.original_filename)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Designer Token Modal */}
      {showDesignerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Enlace para Diseñador</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">URL de Acceso:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={designerUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                  />
                  <Button onClick={() => copyToClipboard(designerUrl)} size="sm">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Token:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={designerToken}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-mono"
                  />
                  <Button onClick={() => copyToClipboard(designerToken)} size="sm">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 mb-1">Instrucciones:</p>
                    <ul className="list-disc list-inside text-blue-800 space-y-1">
                      <li>Comparte la URL completa con el diseñador</li>
                      <li>El token no expira y da acceso solo a subir flyers</li>
                      <li>El diseñador NO podrá acceder al resto del sistema</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <Button onClick={() => setShowDesignerModal(false)}>
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FlyersManager;
